import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import axiosDefault, { AxiosError } from 'axios';
import { dispatch } from 'redux/store';
import { IvrMap, IVRState } from '../../@types/ivr';
import { translator } from 'translation/useTranslator';
import { rejectWithError } from '../dispatchError';

const initialState: IVRState = {
  isLoading: false,
  error: false,
  ivrTypes: [],
  isTestLoading: false,
  ivrToTest: undefined,
  ivrIdToTest: undefined,
  testResponse: undefined
};

const slice = createSlice({
  name: 'ivr',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    setIvrTypes(state, action) {
      state.ivrTypes = action.payload;
    },
    startTest(state) {
      state.isTestLoading = true;
    },
    setTestResponse(state, action) {
      state.isTestLoading = false;
      state.testResponse = action.payload;
    },
    setIvrToTest(state, action) {
      state.ivrToTest = action.payload;
    },
    resetIvrTest(state) {
      state.ivrToTest = undefined;
      state.ivrIdToTest = undefined;
      state.testResponse = undefined;
    }
  }
});

export default slice.reducer;

export const { setIvrToTest, resetIvrTest } = slice.actions;

export const getIVRProviders = () => {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/ivr/providers`);
      dispatch(slice.actions.setIvrTypes(response.data));
      dispatch(slice.actions.stopLoading());
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ivrProvidersFetched() ?? 'IVR providers fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchIvrProviders() ?? `Could not fetch IVR providers`
      });
    }
  };
};

export const getIvrMapToTest = (ivrId: number) => {
  return async () => {
    dispatch(slice.actions.startTest());

    try {
      const response = await axios.get('/ivr/maps/' + ivrId);
      dispatch(slice.actions.setIvrToTest(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ivrMapToTestFetched() ?? 'IVR map to test fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.setTestResponse(error));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchIvrMapToTest() ?? 'Could not fetch IVR map to test'
      });
    }
  };
};

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

const postIvrTest = async ({
  testUrl,
  ivr,
  params
}: {
  testUrl: string;
  ivr?: IvrMap;
  params: { from: string | number; to: string | number };
}) => {
  if (isAbsoluteUrl(testUrl)) {
    return axiosDefault.post(testUrl, ivr, { params });
  }

  const response = await axios.post(testUrl, ivr, {
    params,
    validateStatus: (status) => status !== 401
  });
  if (response.status >= 400) {
    throw new AxiosError(
      `Request failed with status code ${response.status}`,
      AxiosError.ERR_BAD_REQUEST,
      response.config,
      response.request,
      response
    );
  }

  return response;
};

export const testIvrConnection = ({
  ivr,
  testUrl,
  from,
  to
}: {
  ivr?: IvrMap;
  testUrl: string;
  from: number;
  to: number;
}) => {
  return async () => {
    dispatch(slice.actions.startTest());

    const params = {
      from: typeof from === 'number' ? new Date(from)?.toISOString?.() : from,
      to: typeof to === 'number' ? new Date(to)?.toISOString?.() : to
    };

    try {
      const response = await postIvrTest({ testUrl, ivr, params });
      dispatch(slice.actions.setTestResponse(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.ivrConnectionTestReceivedResult() ?? 'IVR connection test received result'
      });
    } catch (error: any) {
      dispatch(slice.actions.setTestResponse(error));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.ivrConnectionTestFailed() ?? 'IVR connection test failed'
      });
    }
  };
};
